import React, { useState } from 'react';
import { makeStyles, tokens, Text, Button, Tab, TabList } from '@fluentui/react-components';
import { 
  ArrowLeft20Regular, 
  MoreHorizontal20Regular,
  People20Filled,
  Clock20Filled,
  CalendarLtr20Filled,
  ChevronDown20Regular
} from '@fluentui/react-icons';
import { CopilotPane } from '../office-shared/copilot';
import { dataVisualizationTokens } from '../../../tokens';
import { InsightsReport } from './InsightsReport';
import CopilotFAB from './CopilotFAB';
import { ExcelIcon } from './ExcelIcon';

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: '#d4e7f7',
    backgroundImage: 'url("/src/assets/backgrounds/Ferris Wheel Photo 1.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    flexDirection: 'column',
  },
  
  // Back button section (second row)
  backSection: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    padding: '12px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    minWidth: 'auto',
    padding: '5px 8px',
    height: '28px',
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
  },
  uploadSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  uploadButton: {
    minWidth: 'auto',
    padding: '5px 12px',
    height: '28px',
    fontSize: '14px',
  },
  uploadedFileName: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    fontWeight: 500,
  },
  hiddenFileInput: {
    display: 'none',
  },
  
  mainContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '24px',
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  mainContentInner: {
    display: 'flex',
    gap: '24px',
    width: '100%',
    maxWidth: '1400px',
    overflow: 'hidden',
    justifyContent: 'center',
  },
  
  // Left content area - now in a card
  contentCard: {
    width: '1200px',
    maxWidth: '1200px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  
  // Tab header section
  tabHeader: {
    padding: '16px 32px 0 32px',
    backgroundColor: 'rgba(255, 255, 255, 0.86)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  
  tabHeaderLeft: {
    flex: 1,
  },
  
  tabHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '8px',
  },
  
  contentScrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 32px',
    // Fluent UI scrollbar styling
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke2,
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: tokens.colorNeutralStroke1,
      },
    },
  },
  
  // Header section with title and badges
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: 'rgba(255, 255, 255, 0.86)',
    marginLeft: '-32px',
    marginRight: '-32px',
    marginTop: '-24px',
    paddingLeft: '32px',
    paddingRight: '32px',
    paddingTop: '24px',
  },
  pageHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  statusLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'white',
    color: '#0e7a0b',
    fontWeight: 600,
    fontSize: '14px',
    padding: '4px 10px',
    borderRadius: '18px',
    border: 'none',
  },
  statusIcon: {
    fontSize: '16px',
    color: '#0e7a0b',
  },
  
  // Action buttons
  primaryActionButton: {
    height: '32px',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '8px',
  },
  splitButtonPrimary: {
    height: '32px',
    fontSize: '14px',
    fontWeight: 600,
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
    borderRight: 'none',
  },
  splitButtonSecondary: {
    height: '32px',
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
    paddingLeft: '6px',
    paddingRight: '6px',
    minWidth: '24px',
  },
  moreButton: {
    height: '32px',
    minWidth: '32px',
    padding: '0',
  },
  
  // Insights Card
  insightsCard: {
    background: 'linear-gradient(50deg, rgba(170, 235, 255, 0.1) 5.72%, rgba(180, 124, 248, 0.1) 92.42%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
    border: '1px solid #4aa1f8',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: 'none',
  },
  insightsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '22px',
  },
  insightsTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  insightsIcon: {
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
  },
  insightsTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  insightsTimestampGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  insightsTimestamp: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
  },
  insightsRefreshIcon: {
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
  },
  insightsContent: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  averageRatingSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: '96px',
    gap: '2px',
  },
  averageRatingLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    fontWeight: 410,
  },
  averageRatingValue: {
    fontSize: '32px',
    fontWeight: 600,
    color: '#464feb',
    lineHeight: '40px',
  },
  averageRatingOutOf: {
    fontSize: '32px',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.4)',
  },
  verticalDivider: {
    width: '1px',
    height: '75px',
    backgroundColor: '#e0e0e0',
    flexShrink: 0,
  },
  insightsList: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  insightItem: {
    fontSize: '14px',
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
    paddingLeft: '0px',
    listStylePosition: 'outside',
    marginLeft: '20px',
  },
  insightStrong: {
    fontWeight: 600,
  },
  insightsFooter: {
    marginTop: '32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  aiDisclaimer: {
    fontSize: '10px',
    color: '#707070',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  },
  getDeeperInsightsButton: {
    height: '28px',
    fontSize: '13px',
    fontWeight: 600,
  },
  
  // Stats cards - using Fluent Card style
  statsRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
  },
  statCard: {
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: tokens.shadow2,
  },
  statLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    minHeight: '18px',
  },
  statLabel: {
    fontSize: '13px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
    lineHeight: '18px',
    marginBottom: '8px',
  },
  checkIndividualsLink: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    textDecoration: 'underline',
    cursor: 'pointer',
    ':hover': {
      color: tokens.colorBrandForeground2,
    },
  },
  statValueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground2,
    opacity: 0.5,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: '36px',
    color: tokens.colorBrandForeground1,
  },
  statUnit: {
    fontSize: '12px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
  },
  statSubtext: {
    fontSize: '12px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground3,
    lineHeight: '16px',
    marginTop: '2px',
  },
  
  // Question card
  questionCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '16px',
    boxShadow: tokens.shadow2,
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  questionNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '20px',
  },
  moreDetailsLink: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  
  // Chart containers
  chartContainer: {
    display: 'flex',
    gap: '48px',
    alignItems: 'center',
  },
  
  // Donut chart
  donutChartSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  donutChart: {
    width: '200px',
    height: '200px',
  },
  
  // Legend
  legendSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  legendLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendText: {
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground1,
  },
  legendCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  
  // Bar chart section
  barChartSection: {
    display: 'flex',
    gap: '32px',
  },
  ratingSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    minWidth: '120px',
  },
  ratingValue: {
    fontSize: '48px',
    fontWeight: 600,
    lineHeight: '56px',
    color: tokens.colorNeutralForeground1,
  },
  ratingLabel: {
    fontSize: '13px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
  },
  starRow: {
    display: 'flex',
    gap: '4px',
  },
  star: {
    fontSize: '20px',
    color: tokens.colorPaletteYellowForeground1,
  },
  
  // Horizontal bars
  barsContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  barLabel: {
    width: '60px',
    fontSize: '13px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
    textAlign: 'right',
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    height: '24px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  barCount: {
    width: '40px',
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    textAlign: 'right',
    flexShrink: 0,
  },
  
  // Right panel - Copilot (matching height)
  copilotCard: {
    width: '380px',
    minWidth: '380px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  
  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.4,
  },
  emptyStateTitle: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '24px',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  emptyStateDescription: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    maxWidth: '400px',
  },
});

interface ViewResponsesProps {
  onBack?: () => void;
  formTitle?: string;
  form?: { intentMetadata?: unknown }; // Form object with intentMetadata
}

export const ViewResponses: React.FC<ViewResponsesProps> = ({ 
  onBack,
  formTitle = "Untitled form",
  form
}) => {
  const styles = useStyles();
  
  // State to control active tab
  const [selectedTab, setSelectedTab] = useState<'overview' | 'insights'>('overview');
  
  // State to control showing insights report (removed, now using tabs)
  // const [showInsightsReport, setShowInsightsReport] = useState(false);
  
  // State to control Copilot pane
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  
  const handleSuggestionClick = (suggestion: string) => {
    // Handle different suggestions
    if (suggestion === "Get key insights") {
      // Open Copilot pane first
      setIsCopilotOpen(true);
      
      // Trigger file upload dialog after a short delay
      setTimeout(() => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.csv';
        fileInput.onchange = async (e: Event) => {
          const target = e.target as HTMLInputElement;
          const file = target.files?.[0];
          if (file) {
            await file.text();
            // TODO: Handle CSV data processing
          }
        };
        fileInput.click();
      }, 300);
    } else if (suggestion === "Send reminders") {
      // Handle send reminders logic
      console.log("Send reminders clicked");
    }
  };
  
  // Remove old insights report conditional render
  // if (showInsightsReport) {
  //   return (
  //     <InsightsReport 
  //       onBack={() => setShowInsightsReport(false)}
  //       formTitle={formTitle}
  //       responseCount={219}
  //     />
  //   );
  // }

  // Sample data matching the design
  const hasResponses = true;
  
  const stats = {
    responses: 72,
    averageTime: "02:20",
    duration: 3,
    durationUnit: "Days"
  };

  const question1Data = {
    number: 1,
    title: "Which company site are you based at?",
    responses: [
      { label: "Beijing", value: 16, count: 16, color: dataVisualizationTokens.qualitative.slot1 },
      { label: "Suzhou", value: 36, count: 36, color: dataVisualizationTokens.qualitative.slot2 },
      { label: "Shanghai", value: 18, count: 18, color: dataVisualizationTokens.qualitative.slot3 },
      { label: "Guangzhou", value: 8, count: 8, color: dataVisualizationTokens.qualitative.slot4 },
    ]
  };

  const question2Data = {
    number: 2,
    title: "How would you rate your overall experience at the Family Day event?",
    averageRating: 4.6,
    responses: [
      { label: "Level 5", value: 50, count: 36 },
      { label: "Level 4", value: 11, count: 8 },
      { label: "Level 3", value: 7, count: 5 },
      { label: "Level 2", value: 10, count: 7 },
      { label: "Level 1", value: 0, count: 0 },
    ]
  };

  return (
    <div className={styles.container}>
      {/* Back to questions button - Second row */}
      <div className={styles.backSection}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          className={styles.backButton}
          onClick={onBack}
        >
          Back to questions
        </Button>
      </div>

      {/* Main Content with Cards */}
      <div className={styles.mainContent}>
        <div className={styles.mainContentInner}>
          {/* Left Content Card */}
          <div className={styles.contentCard}>
            {/* Tab Header with action buttons */}
            <div className={styles.tabHeader}>
              <div className={styles.tabHeaderLeft}>
                <TabList
                  selectedValue={selectedTab}
                  onTabSelect={(_, data) => setSelectedTab(data.value as 'overview' | 'insights')}
                >
                  <Tab value="overview">Response overview</Tab>
                  <Tab value="insights">Insights report</Tab>
                </TabList>
              </div>
              
              <div className={styles.tabHeaderRight}>
                <div style={{ display: 'flex' }}>
                  <Button 
                    appearance="outline" 
                    icon={<ExcelIcon />}
                    className={styles.splitButtonPrimary}
                  >
                    Open in Excel
                  </Button>
                  <Button 
                    appearance="outline" 
                    icon={<ChevronDown20Regular />}
                    className={styles.splitButtonSecondary}
                  />
                </div>
                <Button
                  appearance="subtle"
                  icon={<MoreHorizontal20Regular />}
                  className={styles.moreButton}
                />
              </div>
            </div>
            
          <div className={styles.contentScrollArea}>
            {hasResponses ? (
              <>
                {selectedTab === 'overview' ? (
                  <>

                {/* Stats Cards Row */}
                <div className={styles.statsRow}>
                  <div className={styles.statCard}>
                    <div className={styles.statLabelRow}>
                      <div className={styles.statLabel}>Responses</div>
                      <a className={styles.checkIndividualsLink}>Check individuals</a>
                    </div>
                    <div className={styles.statValueRow}>
                      <div className={styles.statValue}>{stats.responses}</div>
                      <People20Filled className={styles.statIcon} />
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Average time</div>
                    <div className={styles.statValueRow}>
                      <div className={styles.statValue}>{stats.averageTime}</div>
                      <Clock20Filled className={styles.statIcon} />
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statLabel}>Duration</div>
                    <div className={styles.statValueRow}>
                      <div className={styles.statValue}>
                        {stats.duration}<span className={styles.statUnit}> {stats.durationUnit}</span>
                      </div>
                      <CalendarLtr20Filled className={styles.statIcon} />
                    </div>
                  </div>
                </div>

                {/* Question 1: Donut Chart */}
                <div className={styles.questionCard}>
                  <div className={styles.questionHeader}>
                    <div className={styles.questionNumber}>
                      {question1Data.number}. {question1Data.title}
                    </div>
                    <a className={styles.moreDetailsLink}>More details</a>
                  </div>
                  
                  <div className={styles.chartContainer}>
                    {/* Donut Chart SVG */}
                    <div className={styles.donutChartSection}>
                      <svg className={styles.donutChart} viewBox="0 0 200 200">
                        <circle cx="100" cy="100" r="80" fill="none" stroke={question1Data.responses[0].color} strokeWidth="40" strokeDasharray="126 377" transform="rotate(-90 100 100)" />
                        <circle cx="100" cy="100" r="80" fill="none" stroke={question1Data.responses[1].color} strokeWidth="40" strokeDasharray="90 377" strokeDashoffset="-126" transform="rotate(-90 100 100)" />
                        <circle cx="100" cy="100" r="80" fill="none" stroke={question1Data.responses[2].color} strokeWidth="40" strokeDasharray="45 377" strokeDashoffset="-216" transform="rotate(-90 100 100)" />
                        <circle cx="100" cy="100" r="80" fill="none" stroke={question1Data.responses[3].color} strokeWidth="40" strokeDasharray="20 377" strokeDashoffset="-261" transform="rotate(-90 100 100)" />
                      </svg>
                    </div>
                    
                    {/* Legend */}
                    <div className={styles.legendSection}>
                      {question1Data.responses.map((item, index) => (
                        <div key={index} className={styles.legendItem}>
                          <div className={styles.legendLabel}>
                            <div className={styles.legendDot} style={{ backgroundColor: item.color }} />
                            <span className={styles.legendText}>{item.label}</span>
                          </div>
                          <span className={styles.legendCount}>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Question 2: Rating Bar Chart */}
                <div className={styles.questionCard}>
                  <div className={styles.questionHeader}>
                    <div className={styles.questionNumber}>
                      {question2Data.number}. {question2Data.title}
                    </div>
                    <a className={styles.moreDetailsLink}>More details</a>
                  </div>
                  
                  <div className={styles.barChartSection}>
                    {/* Average Rating Display */}
                    <div className={styles.ratingSection}>
                      <div className={styles.ratingValue}>{question2Data.averageRating}</div>
                      <div className={styles.ratingLabel}>Average Rating</div>
                      <div className={styles.starRow}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={styles.star}>
                            {star <= Math.floor(question2Data.averageRating) ? '★' : 
                             star === Math.ceil(question2Data.averageRating) && question2Data.averageRating % 1 >= 0.5 ? '★' : '☆'}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Horizontal Bars */}
                    <div className={styles.barsContainer}>
                      {question2Data.responses.map((item, index) => (
                        <div key={index} className={styles.barRow}>
                          <div className={styles.barLabel}>{item.label}</div>
                          <div className={styles.barTrack}>
                            <div
                              className={styles.barFill}
                              style={{ width: `${item.value}%` }}
                            />
                          </div>
                          <div className={styles.barCount}>{item.count}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                  </>
                ) : (
                  // Insights Report Tab Content
                  <InsightsReport 
                    formTitle={formTitle}
                    responseCount={219}
                    isEmbedded={true}
                    surveyIntent={((form as Record<string, unknown>)?.intentMetadata as Record<string, unknown>)?.intent as string | undefined}
                  />
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📊</div>
                <Text className={styles.emptyStateTitle}>No responses yet</Text>
                <Text className={styles.emptyStateDescription}>
                  Once people start responding to your form, you'll see their responses here. 
                  You can analyze the data, export results, and gain insights.
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Right Copilot Card - Conditionally rendered */}
        {isCopilotOpen && (
          <div className={styles.copilotCard}>
            <CopilotPane
              isOpen={isCopilotOpen}
              onClose={() => setIsCopilotOpen(false)}
              appType="forms"
            />
          </div>
        )}
        </div>
      </div>

      {/* Copilot FAB - Only shown when Copilot pane is closed */}
      {!isCopilotOpen && (
        <CopilotFAB 
          onClick={() => setIsCopilotOpen(true)}
          onSuggestionClick={handleSuggestionClick}
        />
      )}
    </div>
  );
};
